export type AppearanceMode = 'light' | 'dark'
export type TextColourKind = 'primary' | 'muted'

const LEGACY_PRIMARY_KEY = 'opsbridge-primary-text-color'
const LEGACY_MUTED_KEY = 'opsbridge-muted-text-color'

const MODE_KEYS: Record<AppearanceMode, { primary: string; muted: string; workspace: string; workspaceShade: string }> = {
  light: {
    primary: 'opsbridge-light-primary-text-color',
    muted: 'opsbridge-light-muted-text-color',
    workspace: 'opsbridge-light-workspace-color',
    workspaceShade: 'opsbridge-light-workspace-shade',
  },
  dark: {
    primary: 'opsbridge-dark-primary-text-color',
    muted: 'opsbridge-dark-muted-text-color',
    workspace: 'opsbridge-dark-workspace-color',
    workspaceShade: 'opsbridge-dark-workspace-shade',
  },
}

export const DEFAULT_WORKSPACE_COLOUR: Record<AppearanceMode, string> = {
  light: '#3B82F6',
  dark: '#0F172A',
}

export const DEFAULT_WORKSPACE_SHADE = 50

const CUSTOM_THEME_VARS = [
  '--workspace-theme-color',
  '--workspace-page-bg',
  '--dashboard-bg',
  '--dashboard-glow-primary',
  '--dashboard-glow-secondary',
  '--color-background',
  '--color-surface',
  '--color-surface-elevated',
  '--color-border',
  '--color-border-subtle',
  '--color-sidebar-bg',
  '--color-sidebar-active',
  '--color-accent',
  '--color-accent-hover',
  '--workspace-topbar-bg',
  '--workspace-topbar-border',
  '--workspace-sidebar-bg',
  '--workspace-sidebar-border',
  '--workspace-sidebar-shadow',
  '--workspace-sidebar-ornament',
  '--workspace-control-bg',
  '--workspace-control-border',
  '--workspace-icon-tile-bg',
  '--workspace-icon-tile-border',
] as const

function validHex(value: string | null): value is string {
  return !!value && /^#[0-9a-fA-F]{6}$/.test(value)
}

function normaliseHex(value: string) {
  return value.toUpperCase()
}

function safeGet(key: string) {
  try { return localStorage.getItem(key) } catch { return null }
}

function safeSet(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* device storage unavailable */ }
}

function safeRemove(key: string) {
  try { localStorage.removeItem(key) } catch { /* device storage unavailable */ }
}

function hexToRgb(hex: string) {
  const clean = hex.replace('#', '')
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  }
}

function mixHex(from: string, to: string, toWeight: number) {
  const a = hexToRgb(from)
  const b = hexToRgb(to)
  const weight = Math.max(0, Math.min(1, toWeight))
  const mix = (x: number, y: number) => Math.round(x * (1 - weight) + y * weight)
  return '#' + [mix(a.r,b.r), mix(a.g,b.g), mix(a.b,b.b)]
    .map(value => value.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()
}

function rgba(hex: string, alpha: number) {
  const { r, g, b } = hexToRgb(hex)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function workspaceShadeColour(base: string, shade: number) {
  const clamped = Math.max(0, Math.min(100, Number.isFinite(shade) ? shade : DEFAULT_WORKSPACE_SHADE))
  if (clamped === DEFAULT_WORKSPACE_SHADE) return normaliseHex(base)
  if (clamped < DEFAULT_WORKSPACE_SHADE) {
    const weight = ((DEFAULT_WORKSPACE_SHADE - clamped) / DEFAULT_WORKSPACE_SHADE) * 0.42
    return mixHex(base, '#000000', weight)
  }
  const weight = ((clamped - DEFAULT_WORKSPACE_SHADE) / DEFAULT_WORKSPACE_SHADE) * 0.72
  return mixHex(base, '#FFFFFF', weight)
}

export function migrateLegacyTextColours() {
  const legacyPrimary = safeGet(LEGACY_PRIMARY_KEY)
  const legacyMuted = safeGet(LEGACY_MUTED_KEY)

  if (!safeGet(MODE_KEYS.light.primary) && validHex(legacyPrimary)) {
    safeSet(MODE_KEYS.light.primary, normaliseHex(legacyPrimary))
  }
  if (!safeGet(MODE_KEYS.light.muted) && validHex(legacyMuted)) {
    safeSet(MODE_KEYS.light.muted, normaliseHex(legacyMuted))
  }

  safeRemove(LEGACY_PRIMARY_KEY)
  safeRemove(LEGACY_MUTED_KEY)
}

export function readModeTextColour(mode: AppearanceMode, kind: TextColourKind) {
  migrateLegacyTextColours()
  const value = safeGet(MODE_KEYS[mode][kind])
  return validHex(value) ? normaliseHex(value) : null
}

export function writeModeTextColour(mode: AppearanceMode, kind: TextColourKind, value: string) {
  if (!validHex(value)) return
  safeSet(MODE_KEYS[mode][kind], normaliseHex(value))
}

export function clearModeTextColour(mode: AppearanceMode, kind: TextColourKind) {
  safeRemove(MODE_KEYS[mode][kind])
}

export function readModeWorkspaceColour(mode: AppearanceMode) {
  const value = safeGet(MODE_KEYS[mode].workspace)
  return validHex(value) ? normaliseHex(value) : null
}

export function writeModeWorkspaceColour(mode: AppearanceMode, value: string) {
  if (!validHex(value)) return
  safeSet(MODE_KEYS[mode].workspace, normaliseHex(value))
}

export function clearModeWorkspaceColour(mode: AppearanceMode) {
  safeRemove(MODE_KEYS[mode].workspace)
}

export function readModeWorkspaceShade(mode: AppearanceMode) {
  const raw = safeGet(MODE_KEYS[mode].workspaceShade)
  if (raw === null) return null
  const value = Number(raw)
  return Number.isFinite(value) && value >= 0 && value <= 100 ? value : null
}

export function writeModeWorkspaceShade(mode: AppearanceMode, shade: number) {
  if (!Number.isFinite(shade)) return
  safeSet(MODE_KEYS[mode].workspaceShade, String(Math.max(0, Math.min(100, Math.round(shade)))))
}

export function clearModeWorkspaceShade(mode: AppearanceMode) {
  safeRemove(MODE_KEYS[mode].workspaceShade)
}

export function clearAllColourPreferences() {
  safeRemove(LEGACY_PRIMARY_KEY)
  safeRemove(LEGACY_MUTED_KEY)
  for (const mode of ['light','dark'] as AppearanceMode[]) {
    safeRemove(MODE_KEYS[mode].primary)
    safeRemove(MODE_KEYS[mode].muted)
    safeRemove(MODE_KEYS[mode].workspace)
    safeRemove(MODE_KEYS[mode].workspaceShade)
  }
}

function removeCustomThemeVars(root: HTMLElement) {
  for (const key of CUSTOM_THEME_VARS) root.style.removeProperty(key)
}

export function applyResolvedAppearance(mode: AppearanceMode, root: HTMLElement = document.documentElement) {
  migrateLegacyTextColours()

  // Text preferences are mode-specific. A daytime choice must never leak into
  // Night mode unless the user explicitly creates a Night preference.
  root.style.removeProperty('--color-text-primary')
  root.style.removeProperty('--color-text-secondary')
  root.style.removeProperty('--color-text-muted')

  const primary = readModeTextColour(mode, 'primary')
  const muted = readModeTextColour(mode, 'muted')
  if (primary) root.style.setProperty('--color-text-primary', primary)
  if (muted) {
    root.style.setProperty('--color-text-secondary', muted)
    root.style.setProperty('--color-text-muted', muted)
  }

  removeCustomThemeVars(root)
  const storedWorkspace = readModeWorkspaceColour(mode)
  const storedShade = readModeWorkspaceShade(mode)
  if (!storedWorkspace && storedShade === null) return

  const workspaceBase = storedWorkspace || DEFAULT_WORKSPACE_COLOUR[mode]
  const shade = storedShade ?? DEFAULT_WORKSPACE_SHADE
  const workspace = workspaceShadeColour(workspaceBase, shade)

  root.style.setProperty('--workspace-theme-color', workspace)

  if (mode === 'light') {
    // Keep surfaces readable, but let the selected hue stay visibly saturated.
    // The previous build mixed >88% toward white, which made red/purple/green
    // look like nearly identical pastel washes.
    const page = mixHex(workspace, '#FFFFFF', 0.76)
    const bgTop = mixHex(workspace, '#FFFFFF', 0.68)
    const bgMid = mixHex(workspace, '#F8FAFC', 0.78)
    const bgBottom = mixHex(workspace, '#E2E8F0', 0.76)
    const surface = mixHex(workspace, '#FFFFFF', 0.92)
    const elevated = mixHex(workspace, '#F1F5F9', 0.82)
    const border = mixHex(workspace, '#CBD5E1', 0.70)
    const accentHover = mixHex(workspace, '#000000', 0.16)

    root.style.setProperty('--workspace-page-bg', page)
    root.style.setProperty('--color-background', mixHex(workspace, '#FFFFFF', 0.78))
    root.style.setProperty('--dashboard-bg', `linear-gradient(180deg, ${bgTop} 0%, ${bgMid} 52%, ${bgBottom} 100%)`)
    root.style.setProperty('--dashboard-glow-primary', rgba(workspace, 0.28))
    root.style.setProperty('--dashboard-glow-secondary', rgba(workspace, 0.16))
    root.style.setProperty('--color-surface', surface)
    root.style.setProperty('--color-surface-elevated', elevated)
    root.style.setProperty('--color-border', border)
    root.style.setProperty('--color-border-subtle', mixHex(workspace, '#E2E8F0', 0.82))
    root.style.setProperty('--color-sidebar-active', workspace)
    root.style.setProperty('--color-accent', workspace)
    root.style.setProperty('--color-accent-hover', accentHover)
  } else {
    // Night always remains dark. The selected hue is used as a rich tint, not
    // as a light background, so Auto can never turn Night into a bright theme.
    const page = mixHex(workspace, '#05080D', 0.60)
    const bgTop = mixHex(workspace, '#0B1018', 0.48)
    const bgMid = mixHex(workspace, '#080D14', 0.62)
    const bgBottom = mixHex(workspace, '#05080D', 0.74)
    const surface = mixHex(workspace, '#111827', 0.68)
    const elevated = mixHex(workspace, '#1E293B', 0.66)
    const border = mixHex(workspace, '#334155', 0.72)
    const nightAccent = mixHex(workspace, '#FFFFFF', 0.18)
    const accentHover = mixHex(workspace, '#FFFFFF', 0.30)

    root.style.setProperty('--workspace-page-bg', page)
    root.style.setProperty('--color-background', mixHex(workspace, '#080A0E', 0.68))
    root.style.setProperty('--dashboard-bg', `linear-gradient(180deg, ${bgTop} 0%, ${bgMid} 52%, ${bgBottom} 100%)`)
    root.style.setProperty('--dashboard-glow-primary', rgba(workspace, 0.34))
    root.style.setProperty('--dashboard-glow-secondary', rgba(workspace, 0.18))
    root.style.setProperty('--color-surface', surface)
    root.style.setProperty('--color-surface-elevated', elevated)
    root.style.setProperty('--color-border', border)
    root.style.setProperty('--color-border-subtle', mixHex(workspace, '#1E293B', 0.76))
    root.style.setProperty('--color-sidebar-active', nightAccent)
    root.style.setProperty('--color-accent', nightAccent)
    root.style.setProperty('--color-accent-hover', accentHover)
  }
}
